import { useEffect, useState, useRef } from "react";
import { authAxios } from "../../framework/api";
import ReactMarkdown from "react-markdown";
import CustomButton from "../Button";
// import { getAiSajuDummy } from "../../utils/ai-saju-dummy";
import { getAccecssToken, loginTokenKey } from "../../utils/localStorage";
import kakaoImg from "../../assets/images/gpt-ai/kakao_ic.png";
import youtubeImg from "../../assets/images/gpt-ai/youtube_ic.png";
import classImg from "../../assets/images/gpt-ai/class_101_ic.png";

// React Native WebView 타입 선언
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

interface GptAiModalProps {
  gender: string;
  birthDayType: string;
  yearMonthDay: string;
  hourMin: string;
  unknownTime: boolean;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
}

// 링크 추가
const linksText = `

\n\n👉사주풀이 상담 : [상담 신청하기](https://lee-saju.com/payment)
\n\n👉사주 유튜브 : [유튜브 채널](https://www.youtube.com/@%EC%89%AC%EC%9A%B4%EC%82%AC%EC%A3%BC%EC%9D%B4%EA%B7%9C%ED%98%B8)
\n\n👉사주 강의 : [클래스 101](https://class101.net/ko/products/6867b7b243e5efd5deafd720)`;

export default function GptAiModal({
  gender,
  birthDayType,
  yearMonthDay,
  hourMin,
  unknownTime,
  isModalOpen,
  setIsModalOpen,
}: GptAiModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [aiResult, setAiResult] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [displayedText, setDisplayedText] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showLinks, setShowLinks] = useState<boolean>(false);
  const [isUsageExceeded, setIsUsageExceeded] = useState<boolean>(false);
  const hasCalledApi = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const shouldAutoScrollRef = useRef<boolean>(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // shouldAutoScroll 상태가 변경될 때마다 ref 업데이트
  useEffect(() => {
    shouldAutoScrollRef.current = shouldAutoScroll;
  }, [shouldAutoScroll]);

  useEffect(() => {
    console.log("isModalOpen", isModalOpen);

    // 모달이 열려있을 때 배경 스크롤 방지
    if (isModalOpen) {
      // API 호출이 아직 안 된 경우에만 호출
      if (!hasCalledApi.current) {
        getGptAi();
        hasCalledApi.current = true;
      }
      // 배경 스크롤 완전 차단
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
    } else {
      // 스크롤 복원
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "unset";
      document.body.style.height = "unset";
      // 모달이 닫힐 때 플래그 리셋
      hasCalledApi.current = false;
      // 타이핑 애니메이션 상태 리셋
      setDisplayedText("");
      setIsTyping(false);
      setShowLinks(false);
      setIsUsageExceeded(false);
      setShouldAutoScroll(true);
    }

    // 컴포넌트 언마운트 시 스크롤 복원 및 타이머 정리
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "unset";
      document.body.style.height = "unset";

      // 타이머 정리
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isModalOpen]);

  // displayedText나 showLinks가 변경될 때마다 스크롤 업데이트
  useEffect(() => {
    if (contentRef.current && displayedText && shouldAutoScrollRef.current) {
      // requestAnimationFrame을 사용해서 DOM 업데이트 후 스크롤
      requestAnimationFrame(() => {
        if (contentRef.current && shouldAutoScrollRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      });
    }
  }, [displayedText, showLinks]);

  // 스크롤 이벤트 핸들러 (즉시 반응)
  const handleScroll = () => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px 여유
    const isAtTop = scrollTop <= 10; // 맨 위에서 10px 이내

    // 즉시 스크롤 상태 업데이트
    if (isAtBottom) {
      // 사용자가 맨 아래로 스크롤했으면 자동 스크롤 재개
      setShouldAutoScroll(true);
    } else if (isAtTop) {
      // 사용자가 맨 위로 스크롤했을 때
      setShouldAutoScroll(false);
    } else {
      // 사용자가 중간에 스크롤했으면 자동 스크롤 중단
      setShouldAutoScroll(false);
    }
  };

  // 날짜 형식 변환: 19880505 -> 1988-05-05
  const formatBirthday = (dateStr: string) => {
    if (dateStr.length === 8) {
      return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
        6,
        8
      )}`;
    }
    return dateStr;
  };

  // 시간 형식 변환: 0505 -> 05:05
  const formatTime = (timeStr: string) => {
    if (timeStr.length === 4) {
      return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}`;
    }
    return timeStr;
  };

  // 타이핑 애니메이션 함수
  const typeText = (text: string, speed: number = 30) => {
    setIsTyping(true);
    setDisplayedText("");

    let index = 0;
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;

        // 자동 스크롤이 활성화된 경우에만 스크롤
        if (contentRef.current && shouldAutoScrollRef.current) {
          // 현재 스크롤 위치가 맨 아래 근처인지 확인
          const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
          const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20; // 20px 여유

          // 맨 아래 근처에 있을 때만 자동 스크롤
          if (isNearBottom) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }
        }
      } else {
        clearInterval(timer);
        setIsTyping(false);
        // 타이핑 완료 후 링크 표시
        setTimeout(() => {
          setShowLinks(true);
          // 링크 표시 후에도 스크롤이 따라가도록
          if (contentRef.current && shouldAutoScrollRef.current) {
            requestAnimationFrame(() => {
              if (contentRef.current && shouldAutoScrollRef.current) {
                contentRef.current.scrollTop = contentRef.current.scrollHeight;
              }
            });
          }
        }, 500);
      }
    }, speed);

    return () => clearInterval(timer);
  };

  const getGptAi = async () => {
    try {
      setLoading(true);
      setError("");
      setAiResult("");

      const response = await authAxios.post(
        "/mobile/manse/ai",
        {
          gender: gender,
          birthdayType: birthDayType,
          birthday: formatBirthday(yearMonthDay),
          time: unknownTime ? "20:30" : formatTime(hourMin),
          unknownTime: unknownTime,
        },
        {
          headers: {
            Authorization: `Bearer ${getAccecssToken(loginTokenKey)}`,
          },
        }
      );

      // const dummyResponse = await getAiSajuDummy();

      // console.log(response);
      // API 응답에서 실제 데이터 추출 (응답 구조에 따라 조정 필요)
      const data = response.data.data;
      const result = response.data.result;

      // result가 0인 경우 사용량 초과 모달 표시
      if (result === 0) {
        setIsUsageExceeded(true);
        setAiResult("");
      } else {
        setAiResult(data);
        const fullResult = data;

        // 타이핑 애니메이션 시작
        setTimeout(() => {
          typeText(fullResult, 20); // 20ms마다 한 글자씩
        }, 500); // 0.5초 후 시작
      }
    } catch (error) {
      console.log(error);
      setError("AI 사주 해석을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed w-full h-screen top-0 left-0 bg-black/50 flex justify-center items-center p-5 z-[99999]">
      {/* 모달 컨테이너 - 전체 높이 */}
      <section className="bg-white rounded-lg w-full h-full flex flex-col p-5">
        {/* content - 스크롤 가능 */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto text-sm"
          onScroll={handleScroll}
        >
          <div className="space-y-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative">
                    <div className="h-10 w-10 rounded-full border-2 border-gray-200"></div>
                    <div className="absolute inset-0 h-10 w-10 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                  </div>
                  <div className="text-left">
                    <p className="text-gray-700 font-medium">
                      AI가 답변을 준비 중이에요
                    </p>
                    <p className="text-sm text-gray-400">
                      생각을 정리하는 중
                      <span
                        className="inline-block animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      >
                        .
                      </span>
                      <span
                        className="inline-block animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      >
                        .
                      </span>
                      <span
                        className="inline-block animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      >
                        .
                      </span>
                    </p>
                  </div>
                </div>

                {/* Skeleton preview of incoming GPT answer */}
                <div className="w-full max-w-[640px] bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <div className="h-4 w-5/6 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-4 w-4/6 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-4 w-3/6 bg-gray-200 rounded mb-4 animate-pulse"></div>
                  <div className="h-3 w-24 bg-blue-100 rounded animate-pulse"></div>
                </div>

                <p className="mt-4 text-xs text-gray-400">
                  잠시만요 — AI가 내용을 생성하고 있어요.
                </p>
              </div>
            ) : isUsageExceeded ? (
              <div className="text-center py-8 px-4">
                {/* 메인 메시지 */}
                <div className="mb-6 font-extrabold text-black">
                  <p>오늘 가능한 AI 사주 무료 해석</p>
                  <p>사용권을 모두 사용하셨습니다</p>
                </div>

                {/* 부가 메시지 */}
                <div className="mb-8 text-sm text-black">
                  <p>자세한 사주풀이가 필요하다면</p>
                  <p>아래 콘텐츠를 활용해보세요</p>
                </div>

                {/* 버튼들 */}
                <div className="space-y-3 mb-8 font-[Pretendard]">
                  {/* 사주풀이 상담 링크 */}
                  <a
                    href="https://lee-saju.com/payment"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            type: "OPEN_BROWSER",
                            url: "https://lee-saju.com/payment",
                          })
                        );
                      } else {
                        window.open("https://lee-saju.com/payment", "_blank");
                      }
                    }}
                    className="w-full flex justify-center items-center gap-2 rounded-xl p-3 bg-[#EEE6FF] border border-[#9D7FDE] font-semibold text-sm hover:bg-[#E0D6FF] transition-colors"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <img
                      src={kakaoImg}
                      alt="사주풀이 비대면 상담"
                      className="w-5 h-5"
                    />
                    <span>사주풀이 비대면 상담</span>
                  </a>

                  {/* 사주 유튜브 링크 */}
                  <a
                    href="https://www.youtube.com/@%EC%89%AC%EC%9A%B4%EC%82%AC%EC%A3%BC%EC%9D%B4%EA%B7%9C%ED%98%B8"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            type: "OPEN_BROWSER",
                            url: "https://www.youtube.com/@%EC%89%AC%EC%9A%B4%EC%82%AC%EC%A3%BC%EC%9D%B4%EA%B7%9C%ED%98%B8",
                          })
                        );
                      } else {
                        window.open(
                          "https://www.youtube.com/@%EC%89%AC%EC%9A%B4%EC%82%AC%EC%A3%BC%EC%9D%B4%EA%B7%9C%ED%98%B8",
                          "_blank"
                        );
                      }
                    }}
                    className="w-full flex justify-center items-center gap-2 rounded-xl p-3 bg-[#EEE6FF] border border-[#9D7FDE] font-semibold text-sm hover:bg-[#E0D6FF] transition-colors"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <img
                      src={youtubeImg}
                      alt="사주 유튜브"
                      className="w-6 h-6"
                    />
                    <span>사주 유튜브</span>
                  </a>

                  {/* 사주 강의 링크 */}
                  <a
                    href="https://class101.net/ko/products/6867b7b243e5efd5deafd720"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            type: "OPEN_BROWSER",
                            url: "https://class101.net/ko/products/6867b7b243e5efd5deafd720",
                          })
                        );
                      } else {
                        window.open(
                          "https://class101.net/ko/products/6867b7b243e5efd5deafd720",
                          "_blank"
                        );
                      }
                    }}
                    className="w-full flex justify-center items-center gap-2 rounded-xl p-3 bg-[#EEE6FF] border border-[#9D7FDE] font-semibold text-sm hover:bg-[#E0D6FF] transition-colors"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <img src={classImg} alt="사주 강의" className="w-6 h-6" />
                    <span>사주 강의</span>
                  </a>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">😔</div>
                <p className="text-red-500 font-medium mb-2">
                  오류가 발생했습니다
                </p>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : aiResult ? (
              <div className="space-y-4 font-[Pretendard]">
                {/* header - 고정 */}
                <div className="mb-5">
                  <h2 className="font-extrabold text-2xl">AI 사주 해석</h2>
                </div>
                <div className="prose prose-sm max-w-none text-lg text-black">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-3xl font-extrabold mt-8 mb-4">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-2xl font-extrabold mt-6 mb-3">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-xl font-extrabold mt-4 mb-2">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="mb-2 leading-relaxed">{children}</p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-extrabold">{children}</strong>
                      ),
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline break-all"
                          onClick={(e) => {
                            e.preventDefault();
                            // 모바일 웹뷰에서 브라우저로 열기
                            if (window.ReactNativeWebView) {
                              window.ReactNativeWebView.postMessage(
                                JSON.stringify({
                                  type: "OPEN_BROWSER",
                                  url: href,
                                })
                              );
                            } else {
                              // 일반 브라우저에서는 새 탭으로 열기
                              window.open(href, "_blank");
                            }
                          }}
                        >
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {displayedText}
                  </ReactMarkdown>

                  {/* 링크 섹션 - 타이핑 완료 후 표시 */}
                  {showLinks && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline break-all"
                              onClick={(e) => {
                                e.preventDefault();
                                // 모바일 웹뷰에서 브라우저로 열기
                                if (window.ReactNativeWebView) {
                                  window.ReactNativeWebView.postMessage(
                                    JSON.stringify({
                                      type: "OPEN_BROWSER",
                                      url: href,
                                    })
                                  );
                                } else {
                                  // 일반 브라우저에서는 새 탭으로 열기
                                  window.open(href, "_blank");
                                }
                              }}
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {linksText}
                      </ReactMarkdown>
                    </div>
                  )}

                  {/* 타이핑 커서 */}
                  {isTyping && (
                    <span className="inline-block w-0.5 h-5 bg-blue-500 ml-1 animate-pulse"></span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">AI 사주 해석을 시작합니다...</p>
              </div>
            )}
          </div>
        </div>

        {/* button - 고정 */}
        <div className="p-4">
          <CustomButton
            onClick={() => setIsModalOpen(false)}
            className="w-full h-12 font-extrabold !text-lg font-[Pretendard]"
            text={"닫기"}
            primaryType={true}
          />
        </div>
      </section>
    </div>
  );
}
